/**
 * ENG-4644: Sync queue service for optimistic local-first POS writes.
 *
 * Accepts mutation events, persists them to IndexedDB via Dexie,
 * batches them and flushes to the backend via flushNow().
 *
 * PERFORMANCE: Hot-path reads use an in-memory queue (_memQueue).
 * IndexedDB (Dexie) is written to asynchronously for page-reload
 * persistence only — it is NEVER awaited on the flush path.
 */
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { posSyncDb, StoredMutation, MutationType } from './pos-sync-db';
import { LocalOrderStateService } from './local-order-state.service';

@Injectable({ providedIn: 'root' })
export class SyncQueueService implements OnDestroy {
  private endpoint = environment.apiUrl;
  private flushTimer: Subscription | null = null;
  private isFlushing = false;
  private retryCount = 0;
  private maxRetryDelay = 30000; // 30s max
  private destroyed$ = new Subject<void>();

  /** Emitted after a successful reconciliation with fresh server state */
  reconciled$ = new Subject<any>();

  /**
   * In-memory mutation queue: the source of truth during runtime.
   * Key = orderHash, Value = array of pending mutations.
   * IndexedDB is written asynchronously for persistence only.
   */
  private _memQueue: Map<string, StoredMutation[]> = new Map();

  constructor(
    private http: HttpClient,
    private localState: LocalOrderStateService,
    private snackBar: MatSnackBar,
    private ngZone: NgZone,
  ) {}

  /**
   * Boot the queue: replay any pending mutations from a previous session
   * and start the flush timer.
   */
  async init(orderHash: string): Promise<void> {
    // Try to restore order state from Dexie cache (page reload recovery)
    if (!this.localState.snapshot) {
      await this.localState.loadFromCache(orderHash);
    }

    // Replay pending mutations from IndexedDB into local state
    let pending = await posSyncDb.mutations
      .where('orderHash').equals(orderHash)
      .and(m => m.status === 'pending' || m.status === 'syncing')
      .sortBy('timestamp');

    if (pending.length > 0) {
      // Purge stale item-level mutations left over from a previous session.
      const staleIds = pending
        .filter(m => {
          if (m.type === 'add_item' || m.type === 'add_misc_item') return true;
          const itemId = m.payload?.itemId || m.payload?.item_id;
          return typeof itemId === 'string' && (itemId as string).startsWith('local-');
        })
        .map(m => m.id!);

      if (staleIds.length > 0) {
        posSyncDb.mutations.where('id').anyOf(staleIds).delete();
        pending = pending.filter(m => !staleIds.includes(m.id!));
      }

      // Reset any 'syncing' back to 'pending' (interrupted flush)
      const syncingIds = pending.filter(m => m.status === 'syncing').map(m => m.id!);
      if (syncingIds.length > 0) {
        posSyncDb.mutations.where('id').anyOf(syncingIds).modify({ status: 'pending' });
      }

      // Load surviving mutations into in-memory queue
      for (const m of pending) {
        m.status = 'pending';
      }
      this._memQueue.set(orderHash, [...pending]);

      // Replay each surviving mutation optimistically on local state
      for (const m of pending) {
        this._replayMutationLocally(m);
      }
      if (pending.length > 0) {
        this.localState.setSyncing(true);
      }
    }
  }

  /**
   * Mutation types where only the latest value matters.
   * Enqueuing a new one replaces any existing pending mutation of the same type.
   */
  private static LAST_WRITE_WINS: Set<MutationType> = new Set([
    'update_occasion',
    'update_tip',
    'update_customer_info',
    'update_instruction',
    'update_tax_exempt',
    'update_guest_count',
  ]);

  /**
   * Seat moves: only the latest move for the same set of lines matters (tap seat 2,
   * then seat 3 → one mutation). Moves of different lines are kept in order.
   */
  private static seatMoveKey(payload: any): string {
    const ids = (arr: any) => (Array.isArray(arr) ? arr.map(String).sort().join(',') : '');
    return `${ids(payload?.item_ids)}|${ids(payload?.misc_item_ids)}`;
  }

  /**
   * Discount mutations are mutually exclusive — apply cancels remove and
   * vice-versa. Only the latest discount mutation matters.
   */
  private static DISCOUNT_TYPES: Set<MutationType> = new Set([
    'apply_discount',
    'remove_discount',
  ]);

  /**
   * Promo mutations are mutually exclusive — apply cancels remove and
   * vice-versa. Only the latest promo mutation matters.
   */
  private static PROMO_TYPES: Set<MutationType> = new Set([
    'apply_promo',
    'remove_promo',
  ]);

  /**
   * Per-item last-write-wins: only the latest mutation for a given itemId
   * matters (e.g. rapid +/- quantity clicks, re-editing modifiers).
   */
  private static PER_ITEM_LAST_WRITE_WINS: Set<MutationType> = new Set([
    'update_quantity',
    'update_modifiers',
  ]);

  /**
   * Enqueue a new mutation. Called by components after updating local state.
   *
   * Hot path: writes to in-memory queue (instant), then fires Dexie
   * write asynchronously for page-reload persistence.
   */
  async enqueue(
    orderHash: string,
    type: MutationType,
    payload: any,
    tempItemId?: string,
  ): Promise<string> {
    const localId = `local-${this._uuid()}`;
    const mutation: StoredMutation = {
      localId,
      orderHash,
      type,
      payload,
      tempItemId,
      timestamp: Date.now(),
      status: 'pending',
    };

    // ── In-memory deduplication + insert (instant) ──
    let queue = this._memQueue.get(orderHash) || [];

    if (SyncQueueService.LAST_WRITE_WINS.has(type)) {
      queue = queue.filter(m => m.type !== type);
    }
    if (SyncQueueService.DISCOUNT_TYPES.has(type)) {
      queue = queue.filter(m => !SyncQueueService.DISCOUNT_TYPES.has(m.type as MutationType));
    }
    if (SyncQueueService.PROMO_TYPES.has(type)) {
      queue = queue.filter(m => !SyncQueueService.PROMO_TYPES.has(m.type as MutationType));
    }
    if (SyncQueueService.PER_ITEM_LAST_WRITE_WINS.has(type)) {
      const itemId = payload?.itemId || payload?.item_id;
      if (itemId != null) {
        queue = queue.filter(m =>
          !(m.type === type && ((m.payload?.itemId ?? m.payload?.item_id) === itemId))
        );
      }
    }
    if (type === 'set_seat') {
      const key = SyncQueueService.seatMoveKey(payload);
      queue = queue.filter(m => !(m.type === 'set_seat' && SyncQueueService.seatMoveKey(m.payload) === key));
    }

    queue.push(mutation);
    this._memQueue.set(orderHash, queue);
    this.localState.setSyncing(true);

    // ── Async Dexie persistence (fire-and-forget, not on hot path) ──
    const sanitized = JSON.parse(JSON.stringify(mutation));
    this.ngZone.runOutsideAngular(() => {
      // Dedup in Dexie (best-effort, memory is the source of truth)
      const dedup = async () => {
        try {
          if (SyncQueueService.LAST_WRITE_WINS.has(type)) {
            await posSyncDb.mutations.where('orderHash').equals(orderHash)
              .and(m => m.type === type && m.status === 'pending').delete();
          }
          if (SyncQueueService.DISCOUNT_TYPES.has(type)) {
            await posSyncDb.mutations.where('orderHash').equals(orderHash)
              .and(m => SyncQueueService.DISCOUNT_TYPES.has(m.type as MutationType) && m.status === 'pending').delete();
          }
          if (SyncQueueService.PROMO_TYPES.has(type)) {
            await posSyncDb.mutations.where('orderHash').equals(orderHash)
              .and(m => SyncQueueService.PROMO_TYPES.has(m.type as MutationType) && m.status === 'pending').delete();
          }
          if (SyncQueueService.PER_ITEM_LAST_WRITE_WINS.has(type)) {
            const itemId = payload?.itemId || payload?.item_id;
            if (itemId != null) {
              await posSyncDb.mutations.where('orderHash').equals(orderHash)
                .and(m => m.type === type && m.status === 'pending'
                  && ((m.payload?.itemId ?? m.payload?.item_id) === itemId)).delete();
            }
          }
          if (type === 'set_seat') {
            const key = SyncQueueService.seatMoveKey(payload);
            await posSyncDb.mutations.where('orderHash').equals(orderHash)
              .and(m => m.type === 'set_seat' && m.status === 'pending'
                && SyncQueueService.seatMoveKey(m.payload) === key).delete();
          }
          await posSyncDb.mutations.add(sanitized);
        } catch (e) {
          // non-critical: memory queue is the source of truth
        }
      };
      dedup();
    });

    return localId;
  }

  /**
   * Force an immediate flush (called before send-to-kitchen or payment).
   * Returns the server order payload (or null if no mutations/no response).
   */
  async flushNow(refreshOrder = false): Promise<any> {
    if (this.isFlushing) {
      await this._waitForFlush();
    }
    return await this._flush(refreshOrder);
  }

  /**
   * Check if there are pending or in-flight mutations for an order.
   * Used by refreshOrder() to decide whether local snapshot is authoritative.
   */
  hasPending(orderHash: string): boolean {
    const queue = this._memQueue.get(orderHash) || [];
    return queue.some(m => m.status === 'pending' || m.status === 'syncing');
  }

  /**
   * Clean up mutations for an order.
   * When `allStatuses` is true (order cancel/delete), removes ALL mutations
   * including pending ones. Default only removes committed/rejected.
   */
  async cleanup(orderHash: string, allStatuses = false): Promise<void> {
    if (allStatuses) {
      this._memQueue.delete(orderHash);
    }
    // Fire-and-forget Dexie cleanup
    this.ngZone.runOutsideAngular(() => {
      if (allStatuses) {
        posSyncDb.mutations.where('orderHash').equals(orderHash).delete();
      } else {
        posSyncDb.mutations.where('orderHash').equals(orderHash)
          .and(m => m.status === 'committed' || m.status === 'rejected').delete();
      }
    });
  }

  /**
   * Clear ALL queued mutations (memory + Dexie) and stop the flush timer.
   * Called on store switch to prevent old store's mutations from leaking.
   */
  clearAll(): void {
    this._memQueue.clear();
    if (this.flushTimer) {
      this.flushTimer.unsubscribe();
      this.flushTimer = null;
    }
    this.isFlushing = false;
    this.retryCount = 0;
    // Fire-and-forget: wipe all mutations in Dexie
    posSyncDb.mutations.clear().catch(() => {});
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.flushTimer) {
      this.flushTimer.unsubscribe();
    }
  }

  // ─── Private ──────────────────────────────────────

  private _startFlushTimer(): void {
    if (this.flushTimer) this.flushTimer.unsubscribe();
    this.flushTimer = timer(400, 400).subscribe(() => {
      if (!this.isFlushing) {
        this._flush();
      }
    });
  }

  private async _flush(forceRefresh = false): Promise<any> {
    const state = this.localState.snapshot;
    if (!state) return null;

    // ── 1. Read from in-memory queue (instant, no IndexedDB) ──
    const queue = this._memQueue.get(state.orderHash) || [];
    const pending = queue.filter(m => m.status === 'pending')
      .sort((a, b) => a.timestamp - b.timestamp);

    if (pending.length === 0 && !forceRefresh) {
      this.localState.setSyncing(false);
      this.retryCount = 0;
      return null;
    }

    this.isFlushing = true;

    // Build mutations payload from in-memory data (no IndexedDB read)
    const mutations = pending.map(m => ({
      localId: m.localId,
      type: m.type,
      payload: { ...m.payload },
      timestamp: m.timestamp,
      tempItemId: m.tempItemId,
    }));

    try {
      const url = `${this.endpoint}/backend/order/${state.orderHash}/mutations/?full=1`;
      const response: any = await this.http.patch(url, { mutations }).toPromise();

      this.retryCount = 0;

      // Batch-resolve temp→server IDs in a single state update
      const tempIdMappings: Array<{ tempId: string; serverId: number }> = [];
      for (const a of (response.applied || [])) {
        if (a.tempItemId && a.serverId) {
          tempIdMappings.push({ tempId: a.tempItemId, serverId: a.serverId });
        }
      }
      if (tempIdMappings.length > 0) {
        this.localState.resolveItemIds(tempIdMappings);
      }

      // Process rejections (rollback + user notification)
      for (const r of (response.rejected || [])) {
        const rejected = pending.find(m => m.localId === r.localId);
        if (rejected) {
          this._rollback(rejected, r.reason);
        }
      }

      // ── 2. Remove flushed mutations from in-memory queue (instant) ──
      const flushedIds = new Set(pending.map(m => m.localId));
      const remaining = queue.filter(m => !flushedIds.has(m.localId));
      if (remaining.length > 0) {
        this._memQueue.set(state.orderHash, remaining);
      } else {
        this._memQueue.delete(state.orderHash);
      }
      this.localState.setSyncing(remaining.length > 0);

      // Fire-and-forget Dexie cleanup (async, not blocking)
      this.ngZone.runOutsideAngular(() => {
        const localIds = pending.map(m => m.localId);
        posSyncDb.mutations.where('localId').anyOf(localIds).delete()
          .catch(() => {});
      });

      // Reconcile local state with server-computed order
      const serverOrder = response.order || null;
      if (serverOrder) {
        if (serverOrder.items != null) {
          this.localState.reconcileFromServer(serverOrder);
        } else {
          this.localState.updateTotalsFromServer(serverOrder);
        }
        this.reconciled$.next(serverOrder);
      }

      return serverOrder;

    } catch (err) {
      // Network error — mutations stay in memory as pending.
      // Next flushNow() retries them.
      this.retryCount++;
      throw err;
    } finally {
      this.isFlushing = false;
    }
  }

  private async _waitForFlush(): Promise<void> {
    return new Promise(resolve => {
      const check = setInterval(() => {
        if (!this.isFlushing) {
          clearInterval(check);
          resolve();
        }
      }, 50);
    });
  }

  /**
   * Replay a mutation on local state (used on page reload).
   */
  private _replayMutationLocally(m: StoredMutation): void {
    const state = this.localState.snapshot;
    if (!state) return;

    switch (m.type) {
      case 'add_item':
        if (m.payload._menuItem && m.tempItemId) {
          this.localState.addItem(
            m.payload._menuItem,
            m.payload.quantity || 1,
            m.payload.modifiers || [],
            m.payload.special_request || '',
            m.payload.misc_modifiers || [],
            { seat: m.payload.seat, seat_shares: m.payload.seat_shares },
          );
        }
        break;
      case 'update_quantity':
        this.localState.updateQuantity(m.payload.itemId, m.payload.quantity);
        break;
      case 'void_item':
        this.localState.voidItem(m.payload.itemId);
        break;
      case 'apply_discount':
        this.localState.applyDiscount(
          m.payload.type,
          m.payload.promotional_discount,
          m.payload.description,
        );
        break;
      case 'remove_discount':
        this.localState.removeDiscount();
        break;
      case 'update_tip':
        if (m.payload.tip_percent != null) {
          this.localState.updateTip(m.payload.tip_percent, null);
        } else {
          this.localState.updateTip(null, m.payload.tip);
        }
        break;
      case 'add_misc_item':
        if (m.payload.items) {
          for (const item of m.payload.items) {
            this.localState.addMiscItem(item);
          }
        }
        break;
      case 'delete_misc_item':
        this.localState.deleteMiscItem(m.payload.miscItemId);
        break;
      case 'update_occasion':
        this.localState.updateOccasion(m.payload);
        break;
      case 'update_customer_info':
        this.localState.updateCustomerInfo(m.payload.phone_number, m.payload.order_name);
        break;
      case 'update_instruction':
        this.localState.updateInstruction(m.payload.special_request, m.payload.order_name);
        break;
      case 'update_tax_exempt':
        this.localState.updateTaxExempt(m.payload.is_tax_exempt);
        break;
      case 'apply_promo':
        if (m.payload.promo_code) {
          this.localState.applyPromo(m.payload.promo_code, m.payload.discount || 0);
        }
        break;
      case 'remove_promo':
        this.localState.removePromo();
        break;
      case 'set_seat':
        this.localState.setSeat(
          m.payload.item_ids || [],
          m.payload.misc_item_ids || [],
          m.payload.seat ?? null,
          Array.isArray(m.payload.seat_shares) ? m.payload.seat_shares : null,
        );
        break;
      case 'update_guest_count':
        if (m.payload.guest_count != null) {
          this.localState.updateGuestCount(m.payload.guest_count);
        }
        break;
    }
  }

  /**
   * Rollback a rejected mutation and notify the user.
   */
  private _rollback(mutation: StoredMutation, reason: string): void {
    switch (mutation.type) {
      case 'add_item':
        if (mutation.tempItemId) {
          this.localState.rollbackAddItem(mutation.tempItemId);
        }
        break;
      case 'update_quantity':
      case 'void_item':
      case 'apply_discount':
      case 'remove_discount':
      case 'update_tip':
      case 'update_occasion':
      case 'apply_promo':
      case 'remove_promo':
      case 'set_seat':
      case 'update_guest_count':
        // Server reconciliation will fix these
        break;
      case 'add_misc_item':
        if (mutation.tempItemId) {
          this.localState.rollbackMiscItem(mutation.tempItemId);
        }
        break;
      case 'delete_misc_item':
        // Server reconciliation will restore the item
        break;
    }

    const itemName = mutation.payload?._menuItemName || mutation.payload?.menuItemName || 'item';
    this.snackBar.open(
      `Couldn't process ${itemName} — ${reason}. Please try again.`,
      'OK',
      { verticalPosition: 'top', duration: 5000 },
    );
  }

  private _uuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
